import { ChevronDown } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

/** Card com header clicável que expande/recolhe o conteúdo.
 *
 * Feito local da rota `/superadmin/settings/platform` — 3 cards longos
 * (Contratada com 7 campos, Corpo da proposta com 6 campos e help, Rodapé)
 * ficavam cansativos de scrollar. Alexandre pediu collapse/expand pra
 * focar numa seção por vez.
 *
 * **Não usa o `PageSectionCard`** do `@kmgestao/ui` porque ele renderiza
 * `<CardContent>` sempre — mesmo quando vazio, o padding bottom aparece.
 * Aqui construo com Card primitives direto pra remover o CardContent
 * inteiro quando colapsado (animação futura pode vir com Radix Collapsible,
 * por ora é toggle binário simples).
 *
 * Acessibilidade:
 *   - `aria-expanded` e `aria-controls` no botão do header.
 *   - Enter/Space no botão alterna (comportamento nativo de `<button>`).
 *   - Content com `id` vinculado, escondido via `hidden` quando fechado
 *     (screen readers pulam em vez de anunciar).
 *
 * Se o pattern virar comum em outras páginas do superadmin, vale promover
 * pro `@kmgestao/ui/patterns/CollapsibleSectionCard`. */
export function CollapsibleSectionCard({
  title,
  description,
  children,
  defaultOpen = false,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Inicia recolhido por default (decisão do Alexandre 2026-04-24):
   * a página de /settings/platform tem 3 cards longos; abrir tudo de
   * uma vez polui o scroll. Karla clica no header pra expandir só o
   * que vai editar. */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <div className="rounded-lg border border-border bg-card shadow-md shadow-foreground/5 transition-shadow duration-200 hover:shadow-xl hover:shadow-foreground/10 dark:shadow-xl dark:shadow-black/30 dark:hover:shadow-2xl dark:hover:shadow-black/50">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={contentId}
        className="flex w-full items-start justify-between gap-4 rounded-t-lg p-6 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <div className="space-y-2">
          <h3 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
            {title}
          </h3>
          {description ? (
            <p className="text-base leading-7 text-muted-foreground">{description}</p>
          ) : null}
        </div>
        <ChevronDown
          aria-hidden="true"
          className={`mt-1 size-5 shrink-0 text-muted-foreground transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open ? (
        <div id={contentId} className="px-6 pb-6">
          {children}
        </div>
      ) : null}
    </div>
  );
}
